import {
  BannerType,
  ContentFulScreens,
  NotificationType,
  Screen,
  useApp,
  useCms,
  useTranslatedTag,
  useTranslation,
  useUserInfo,
  useUserInteractions,
  useUserSettings,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE } from "@next-platform/client-common-core/infra/constants";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import useCanAttachRulebook from "@next-platform/client-common-core/rulebook/attach/useCanAttachRulebook";
import { CharacterCardActions as CharacterCardActionsV2, ListEmpty } from "@next-platform/client-common-spicy-ui";
import { Lorebook, Rulebook } from "@next-platform/packages-ts-types";
import cx from "classnames";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHits, useInstantSearch } from "react-instantsearch";
import { useNavigate, useNavigationType } from "react-router-dom";

import {
  AgeVerificationWarningModal,
  BlockCreatorModal,
  CapabilityShareCharacterModal,
  CapabilityShareLorebookModal,
  CapabilityShareRulebookModal,
  CmsBanner,
  CmsBannerService,
} from "@/components";
import CharacterCard from "@/components/Character/CharacterCard/variants";
import { getOfflineNotificationMessage } from "@/components/common/Notification/utils/notification";
import ScreenLoader from "@/components/customComponents/ScreenLoader";
import ErrorSection from "@/components/ErrorSection/ErrorSection";
import RulebookAttachTargetModal from "@/components/Rulebook/RulebookAttachTargetModal";
import useApi from "@/components/useApi";
import {
  CHATBOT_INTERACTION_EVENT_MAP,
  DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM,
  FEATURE_FLAGS,
  HIDE_RECOMMENDATION_DELAY_MS,
  HOME_LAST_CLICKED_CHARACTER_SESSION_STORAGE_KEY,
  HOMEPAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX,
  NSFW_LOCAL_STORAGE_KEY,
  SFW_LOCAL_STORAGE_KEY,
} from "@/constants";
import { ROUTES } from "@/constants/navigation";
import { useAgeVerificationFlags, useFeatureFlag, useRecombee } from "@/hooks";
import { IRecommItems } from "@/hooks/types";
import { useCenterElementDetection } from "@/hooks/useCenterElementDetection";
import useCharacterCardGridConfig from "@/hooks/useCharacterCardGridConfig";
import useCloneRulebook from "@/hooks/useCloneRulebook";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import useToast from "@/hooks/useToast";
import { CharacterRatingWeights } from "@/pages/Chat/components/constants";
import LorebookCard from "@/pages/LorebookMy/LorebookCard";
import useRulebookDraftOwner from "@/pages/RulebookEditor/hooks/useRulebookDraftOwner";
import RulebookCard from "@/pages/RulebookMy/RulebookCard";
import useAuth from "@/services/useAuth";
import { Collection } from "@/types/api";
import { HitConfig, HitItem } from "@/types/hits";
import { getBusinessRouteScreen, getNumberOfCols, removeRecommendationFromLocalStorage } from "@/utils";
import { getCharacterHits } from "@/utils/getCharacterHits";

import useInstantSearchUrlStateSync from "./hooks/useInstantSearchUrlStateSync";
import { CharacterCardDropdownActions } from "./types";
import filterFavoriteLorebookHits from "./utils/filterFavoriteLorebookHits";
import getCharacterHitsViewState from "./utils/getCharacterHitsViewState";
import { getHitConfig } from "./utils/getHitConfig";
import type { IRulebookHitFields } from "./utils/getHitRulebookConfig";
import hasRemainingFavoriteLorebooks from "./utils/hasRemainingFavoriteLorebooks";

const SCROLL_LISTENER_ATTACH_DELAY_MS = 100;

const shareCapabilityModalFor = (collection: Collection) => {
  if (collection === Collection.LOREBOOKS) {
    return CapabilityShareLorebookModal;
  }
  if (collection === Collection.RULEBOOKS) {
    return CapabilityShareRulebookModal;
  }
  return CapabilityShareCharacterModal;
};

/** Path segment of the share URL — mirrors the public detail route each collection will expose. */
const shareSegmentFor = (collection: Collection): string => {
  if (collection === Collection.LOREBOOKS) {
    return "lorebook";
  }
  if (collection === Collection.RULEBOOKS) {
    return "rulebook";
  }
  return "chatbot";
};

const shareAllowedFor = (
  collection: Collection,
  isCharacterShareLinkAllowed: boolean,
  isLorebookShareLinkAllowed: boolean,
  isRulebookPublicSharingAllowed: boolean,
): boolean => {
  if (collection === Collection.LOREBOOKS) {
    return isLorebookShareLinkAllowed;
  }
  if (collection === Collection.RULEBOOKS) {
    return isRulebookPublicSharingAllowed;
  }
  return isCharacterShareLinkAllowed;
};

const reportRouteFor = (collection: Collection): string => {
  if (collection === Collection.LOREBOOKS) {
    return ROUTES.reportLorebook;
  }
  if (collection === Collection.RULEBOOKS) {
    return ROUTES.reportRulebook;
  }
  return ROUTES.report;
};

const retainPendingLorebookIds = (previousIds: Set<string>, likedLorebookIds: string[] | undefined) => {
  const nextIds = new Set([...previousIds].filter((lorebookId) => likedLorebookIds?.includes(lorebookId)));
  return nextIds.size === previousIds.size ? previousIds : nextIds;
};

const usePendingRemovedLorebookIds = (likedLorebookIds: string[] | undefined) => {
  const [statePendingRemovedLorebookIds, setStatePendingRemovedLorebookIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setStatePendingRemovedLorebookIds((previousIds) => retainPendingLorebookIds(previousIds, likedLorebookIds));
  }, [likedLorebookIds]);

  const markPendingRemoved = useCallback((lorebookId: string) => {
    setStatePendingRemovedLorebookIds((previousIds) => new Set(previousIds).add(lorebookId));
  }, []);

  const unmarkPendingRemoved = useCallback((lorebookId: string) => {
    setStatePendingRemovedLorebookIds((previousIds) => {
      const nextIds = new Set(previousIds);
      nextIds.delete(lorebookId);
      return nextIds;
    });
  }, []);

  return { statePendingRemovedLorebookIds, markPendingRemoved, unmarkPendingRemoved };
};

interface ICharacterHitsProps {
  isPartner: boolean;
  nsfwToggle: boolean;
  recommId: string;
  currentCategory: string;
  searchIndexName: string;
  refetchingRecommendations?: boolean;
  setIsCharactersLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  collection: Collection;
  shouldFilterNsfwCharacters: boolean;
}

interface IEmptyState {
  title: string;
  description: string;
  buttonText?: string;
  onButtonPress?: () => void;
}

const Hits = React.memo(({ isPartner, collection = Collection.CHARACTERS, searchIndexName, ...props }: ICharacterHitsProps) => {
  useInstantSearchUrlStateSync({ searchIndexName });

  const { t } = useTranslation();
  const { getTranslatedTag } = useTranslatedTag();
  const refCharacterCardContainerRef = useRef<HTMLDivElement>(null);
  const { isAgeVerificationRequired } = useAgeVerificationFlags();
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const { getInlineBannersRowMapping } = useCms();
  const { recommId, nsfwToggle, currentCategory, setIsCharactersLoading, refetchingRecommendations = false, shouldFilterNsfwCharacters } = props;
  const { getCachedRecommendations, sendUserInteractionAddBookmark, recombeeUserId, sendUserInteractionDetailView, sendUserInteractionRating } =
    useRecombee();
  const [stateIsAgeVerificationVisible, setStateIsAgeVerificationVisible] = useState<boolean | undefined>(undefined);
  const { logEvent } = useQueueLogEvent();
  const [stateRecommendations, setStateRecommendations] = useState<IRecommItems | null>(null);
  const { items: hits, results } = useHits();
  const { callApi, jsonApiService } = useApi();
  const { displayToast } = useToast();
  const navigationType = useNavigationType();
  const refUserHasScrolledRef = useRef(false);
  // Capture how this mount was reached once, on first render. The live
  // navigationType flips from "POP" to "REPLACE" while hits are still loading,
  // because InstantSearch serializes tag refinements back into the URL
  // (routing.push -> navigate replace). Reading the live value in the restore
  // effect below would miss the back-navigation window and reset scroll to top.
  const refWasBackNavigationRef = useRef(navigationType === "POP");
  const selectedTags = results?._state.facetsRefinements.tags;
  const searchedQuery = results?.query;
  const navigate = useNavigate();
  const localizePathname = useLocalizedPathname();
  const isTagsSelected = !!selectedTags?.length;
  const isSearchClientFirstPage = results?.page === 0;
  const { pagePath, search } = useLocalizedLocation();
  const screenName = getBusinessRouteScreen(pagePath) as Screen;
  const { isLoading: isAuthLoading, isAuthenticated } = useAuth();
  const { userId, onlyUserId } = useUserInfo();
  // Scope the draft store to this account before a Rulebook clone writes to it.
  const isDraftOwnerReady = useRulebookDraftOwner(onlyUserId);
  const {
    likedBots,
    likedLorebooks,
    blockedUsers,
    selectedTags: selectedTagsStore,
    lastTimeFilteredCharacters,
    onUserLikebot,
    onUserLikeLorebook,
    blurNSFW,
    chatLanguage,
  } = useUserSettings();
  const { recombeeRecommendationsConfig } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const {
    homepageScenarioName: scenario,
    homepageRecommendationsFetchCount,
    homepageRecommendationsDislikedAllowedToRemoveCount,
    recommendationspageFetchCount,
    recommendationspageDislikedAllowedToRemoveCount,
  } = recombeeRecommendationsConfig ?? {};
  const homepageRecommCount = homepageRecommendationsFetchCount ?? DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM;
  const { isSearchInProgress, sortBy } = useUserInteractions();

  const { status, error } = useInstantSearch({ catchError: true });

  const isHitsLoading = status === "loading" || status === "stalled";
  const isFilteredTimerExpired = !lastTimeFilteredCharacters || new Date().getTime() - lastTimeFilteredCharacters > HIDE_RECOMMENDATION_DELAY_MS;

  const isSortByDefault = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const sortByVal = searchParams.get("dev_characters[sortBy]") || searchParams.get("characters[sortBy]");
    return sortBy === null && sortByVal === null;
  }, [sortBy, search]);

  const shouldInjectRecommendations =
    userId &&
    hits?.length > 0 &&
    (pagePath === "/" || pagePath === "/external") &&
    isFilteredTimerExpired &&
    isSearchClientFirstPage &&
    !isSearchInProgress &&
    isSortByDefault;

  const [stateCharacterInfo, setStateCharacterInfo] = useState<HitItem | null>(null);
  const [stateIsShareModalOpen, setStateIsShareModalOpen] = useState(false);
  const [isCharacterShareLinkAllowed, isLorebookShareLinkAllowed, isRulebookPublicSharingAllowed] = useApplicationCapabilitiesAllowed([
    SubCapabilityKey.CharacterShareLink,
    SubCapabilityKey.LorebookShareLink,
    SubCapabilityKey.RulebookPublicSharing,
  ]);
  const [stateIsBlockModalOpen, setStateIsBlockModalOpen] = useState(false);
  const { canAttach: canAttachRulebook } = useCanAttachRulebook();
  const [stateAttachRulebook, setStateAttachRulebook] = useState<{ id: string; name: string } | null>(null);
  const { statePendingRemovedLorebookIds, markPendingRemoved, unmarkPendingRemoved } = usePendingRemovedLorebookIds(likedLorebooks);

  const characterId =
    stateCharacterInfo?.character_id ||
    stateCharacterInfo?.lorebook_id ||
    stateCharacterInfo?.id ||
    (stateCharacterInfo as (HitItem & Partial<IRulebookHitFields>) | null)?.rulebook_id;
  const { centeredElementIds, setElementRef, elementRefs } = useCenterElementDetection();

  const { colWidth, colGap } = useCharacterCardGridConfig();
  const containerWidth = refCharacterCardContainerRef.current?.clientWidth || 0;
  const numberOfColumns = useMemo(() => getNumberOfCols({ containerWidth, colWidth, colGap }), [containerWidth, colWidth, colGap]);
  const [stateDislikedRecommendedCharacterIds, setStateDislikedRecommendedCharacterIds] = useState<string[]>([]);

  useEffect(() => {
    if (error && !navigator.onLine) {
      displayToast({ message: getOfflineNotificationMessage(t), type: NotificationType.warning });
    }
  }, [displayToast, error, t]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!shouldInjectRecommendations || isAuthLoading) {
        return;
      }
      const nsfwKey = nsfwToggle ? NSFW_LOCAL_STORAGE_KEY : SFW_LOCAL_STORAGE_KEY;

      const fetchedRecommendations = await getCachedRecommendations({
        storageKey: `${HOMEPAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX}_${nsfwKey}`,
        timeOut: Number(RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE),
        userId,
        scenario,
        recommendationsCount: homepageRecommCount,
        blockedUsers,
        selectedTags: selectedTagsStore,
        nsfwToggle,
        applicationId,
      });

      setStateRecommendations(fetchedRecommendations);
    };

    void fetchRecommendations();
  }, [
    shouldInjectRecommendations,
    isAuthLoading,
    userId,
    nsfwToggle,
    scenario,
    homepageRecommCount,
    blockedUsers,
    selectedTagsStore,
    applicationId,
    getCachedRecommendations,
  ]);

  const charHits = getCharacterHits({
    hits: hits as unknown as HitItem[],
    isAuthLoading,
    shouldInjectRecommendations: !!shouldInjectRecommendations,
    recommendations: stateRecommendations,
    recommId,
    searchedQuery,
    shouldFilterNsfwCharacters,
  });

  const handleFavorite = useCallback(
    async (characterId: string) => {
      const likedBot = characterId;
      const isLiked = !likedBots?.includes(characterId);
      await onUserLikebot(likedBot, screenName as Screen);
      if (isLiked) {
        await sendUserInteractionAddBookmark(recombeeUserId, characterId);
      }
    },
    [likedBots, callApi, jsonApiService, recombeeUserId, isHitsLoading, isAuthLoading],
  );

  const handleFavoriteLorebook = useCallback(
    async (lorebookId: string) => {
      const isRemovingFavorite = !!likedLorebooks?.includes(lorebookId);
      if (isRemovingFavorite && pagePath === ROUTES.favoritesLorebooks) {
        markPendingRemoved(lorebookId);
      }

      try {
        const isLiked = !isRemovingFavorite;
        await onUserLikeLorebook(lorebookId, screenName as Screen);
        if (isLiked) {
          await sendUserInteractionAddBookmark(recombeeUserId, lorebookId);
        }
      } catch {
        unmarkPendingRemoved(lorebookId);
        displayToast({
          message: t("common:somethingWentWrong"),
          type: NotificationType.error,
        });
      }
    },
    [
      likedLorebooks,
      pagePath,
      onUserLikeLorebook,
      screenName,
      sendUserInteractionAddBookmark,
      recombeeUserId,
      displayToast,
      t,
      markPendingRemoved,
      unmarkPendingRemoved,
    ],
  );

  const handleCloseBlockModal = () => {
    setStateIsBlockModalOpen(false);
  };

  const handleCloseAttachRulebook = useCallback(() => setStateAttachRulebook(null), []);

  const handleCharacterInfo = useCallback(
    (charId: string) => {
      let charData;
      if (characterId !== charId) {
        charData = charHits?.find(
          (char) =>
            char?.character_id === charId ||
            char?.lorebook_id === charId ||
            char?.id === charId ||
            (char as HitItem & Partial<IRulebookHitFields>)?.rulebook_id === charId,
        );

        setStateCharacterInfo(charData ?? null);
      }
      return charData || stateCharacterInfo;
    },
    [characterId, stateCharacterInfo, charHits],
  );

  const handleCharacterChatInteraction = (characterId: string) => {
    const charData = handleCharacterInfo(characterId);
    void sendUserInteractionDetailView(recombeeUserId, characterId, {
      recommId: charData?.recommId,
    });
    sessionStorage.setItem(HOME_LAST_CLICKED_CHARACTER_SESSION_STORAGE_KEY, characterId);
  };

  const handleLessLikeThisAction = (characterId: string) => {
    const isRecommendedCharacter = handleCharacterInfo(characterId)?.recommId;
    if (isRecommendedCharacter) {
      setStateDislikedRecommendedCharacterIds((prev) => [...prev, characterId]);
      removeRecommendationFromLocalStorage({
        characterId,
        nsfwToggle,
        homepageRecommendationsFetchCount,
        homepageRecommendationsDislikedAllowedToRemoveCount,
        recommendationspageFetchCount,
        recommendationspageDislikedAllowedToRemoveCount,
      });
    }
    void sendUserInteractionRating(userId as string, characterId, CharacterRatingWeights.DISLIKE);
  };

  const { clone, isCloning } = useCloneRulebook();
  const handleRulebookClone = useCallback(
    (rulebookId: string) => {
      if (!isDraftOwnerReady || !onlyUserId) {
        return;
      }
      clone(rulebookId);
    },
    [isDraftOwnerReady, onlyUserId, clone],
  );

  const characterCardActionsHandler = useCallback(
    async (action: CharacterCardActionsV2 | CharacterCardDropdownActions, id: string, isNsfw?: boolean) => {
      if (isNsfw && isAgeVerificationRequired) {
        setStateIsAgeVerificationVisible(true);
        return;
      }
      const isShareAllowed = shareAllowedFor(collection, isCharacterShareLinkAllowed, isLorebookShareLinkAllowed, isRulebookPublicSharingAllowed);
      if (action === CharacterCardDropdownActions.SHARE && !isShareAllowed) return;

      const eventName = CHATBOT_INTERACTION_EVENT_MAP[action as keyof typeof CHATBOT_INTERACTION_EVENT_MAP];
      if (eventName) {
        logEvent(eventName, {
          bot_category: currentCategory,
          character_id: id,
          page_name: screenName,
        });
      }

      switch (action) {
        case CharacterCardActionsV2.INFO:
          return sessionStorage.setItem(HOME_LAST_CLICKED_CHARACTER_SESSION_STORAGE_KEY, id);
        case CharacterCardActionsV2.CHAT:
          return handleCharacterChatInteraction(id);
        case CharacterCardActionsV2.FAVORITE:
          // Rulebooks have no favourites backend — the action is never surfaced on their cards.
          if (collection === Collection.RULEBOOKS) {
            return;
          }
          if (collection === Collection.LOREBOOKS) {
            return await handleFavoriteLorebook(id);
          }
          return await handleFavorite(id);
        case CharacterCardDropdownActions.SHARE:
          handleCharacterInfo(id);
          return setStateIsShareModalOpen(true);
        case CharacterCardDropdownActions.BLOCK:
          handleCharacterInfo(id);
          return setStateIsBlockModalOpen(true);
        case CharacterCardDropdownActions.REPORT:
          return navigate(`${reportRouteFor(collection)}/${id}`);
        case CharacterCardDropdownActions.LESS_LIKE_THIS:
          return handleLessLikeThisAction(id);
        case CharacterCardDropdownActions.CLONE:
          return handleRulebookClone(id);
        case CharacterCardDropdownActions.ATTACH: {
          const rulebookHit = handleCharacterInfo(id);
          setStateAttachRulebook({ id, name: rulebookHit?.name ?? "" });
          return;
        }
      }
    },
    [
      likedBots,
      recombeeUserId,
      charHits,
      stateDislikedRecommendedCharacterIds,
      collection,
      handleFavoriteLorebook,
      handleFavorite,
      isCharacterShareLinkAllowed,
      isLorebookShareLinkAllowed,
      isRulebookPublicSharingAllowed,
      handleRulebookClone,
    ],
  );

  const characterCardActionsHandlerForHitConfig = useCallback(
    (action: CharacterCardActionsV2 | CharacterCardDropdownActions, id: string, isNsfw?: boolean) => {
      void characterCardActionsHandler(action, id, isNsfw);
    },
    [characterCardActionsHandler],
  );

  const memoizedData: HitConfig[] = useMemo(() => {
    if (!charHits) {
      return [];
    }
    return getHitConfig({
      collection,
      hits: charHits,
      likedBots: likedBots,
      likedLorebooks,
      onlyUserId,
      chatLanguage,
      dislikedRecommendedCharacterIds: stateDislikedRecommendedCharacterIds,
      localizePathname,
      t,
      translateTag: getTranslatedTag,
      blurNSFW,
      isPartner,
      isAgeVerificationRequired,
      isAuthenticated,
      canAttachRulebook,
      hasVoiceAttachmentFeatureFlag,
      characterCardActionsHandler: characterCardActionsHandlerForHitConfig,
      isRulebookDraftReady: isDraftOwnerReady && !!onlyUserId,
    });
  }, [
    charHits,
    likedBots,
    likedLorebooks,
    onlyUserId,
    chatLanguage,
    collection,
    stateDislikedRecommendedCharacterIds,
    localizePathname,
    t,
    getTranslatedTag,
    blurNSFW,
    isPartner,
    isAgeVerificationRequired,
    isAuthenticated,
    canAttachRulebook,
    hasVoiceAttachmentFeatureFlag,
    characterCardActionsHandlerForHitConfig,
    isDraftOwnerReady,
  ]);

  const visibleHits = useMemo(
    () =>
      filterFavoriteLorebookHits({
        hits: memoizedData,
        isFavoritesLorebooksPage: pagePath === ROUTES.favoritesLorebooks,
        likedLorebookIds: likedLorebooks ?? [],
        pendingRemovedLorebookIds: statePendingRemovedLorebookIds,
      }),
    [memoizedData, pagePath, likedLorebooks, statePendingRemovedLorebookIds],
  );

  const defaultEmptyState = {
    title: t("common:noResultsFound"),
    description: t("search:chatbots.noResults.description"),
  } as IEmptyState;

  const emptyStates = {
    [ROUTES.favoriteBots]: {
      title: defaultEmptyState.title,
      description: t("search:chatbots.favorites.noResults.description"),
      buttonText: t("search:chatbots.favorites.noResults.cta.exploreChatbots"),
      onButtonPress: () => {
        navigate("/");
      },
    },
    [ROUTES.favoritesLorebooks]: {
      title: defaultEmptyState.title,
      description: t("search:lorebooks.favorites.noResults.description"),
      buttonText: t("search:lorebooks.favorites.noResults.cta.exploreLorebooks"),
      onButtonPress: () => {
        navigate(localizePathname(ROUTES.lorebooksExplore));
      },
    },
    [ROUTES.favoritesGroups]: {
      title: defaultEmptyState.title,
      description: t("search:groups.favorites.noResults.description"),
      buttonText: t("search:groups.favorites.noResults.cta.exploreGroups"),
      onButtonPress: () => {
        navigate("/");
      },
    },
    [ROUTES.home]: defaultEmptyState,
    [ROUTES.recommendedBots]: defaultEmptyState,
  };

  const emptyState = emptyStates[pagePath as keyof typeof emptyStates] ?? defaultEmptyState;
  const isSearchResultPending = !results || !!results.__isArtificial;

  const hasFavoriteLorebooks = hasRemainingFavoriteLorebooks({
    likedLorebookIds: likedLorebooks ?? [],
    pendingRemovedLorebookIds: statePendingRemovedLorebookIds,
  });
  const hasNoFavoriteLorebooks = pagePath === ROUTES.favoritesLorebooks && !hasFavoriteLorebooks;
  const { isDataLoading, hasNoResults } = getCharacterHitsViewState({
    isSearchResultPending,
    isAuthLoading,
    isHitsLoading,
    characterHitsCount: charHits?.length,
    searchHitsCount: hits?.length ?? 0,
    selectedTagsCount: selectedTags?.length,
    searchedQuery,
    refetchingRecommendations,
    hasNoFavoriteLorebooks,
  });

  useEffect(() => {
    if (setIsCharactersLoading) {
      setIsCharactersLoading(isDataLoading);
    }
  }, [isDataLoading]);

  // Restore scroll to last viewed character on back navigation
  useLayoutEffect(() => {
    const lastCharacterId = sessionStorage.getItem(HOME_LAST_CLICKED_CHARACTER_SESSION_STORAGE_KEY);
    const isBackNavigation = refWasBackNavigationRef.current;

    if (isBackNavigation && lastCharacterId && charHits?.length) {
      if (!refUserHasScrolledRef.current) {
        const element = elementRefs.current[lastCharacterId];
        element?.scrollIntoView({ block: "center", behavior: "instant" });
      }

      if (!shouldInjectRecommendations || stateRecommendations) {
        sessionStorage.removeItem(HOME_LAST_CLICKED_CHARACTER_SESSION_STORAGE_KEY);
        refUserHasScrolledRef.current = false;
      }

      const handleScroll = () => {
        refUserHasScrolledRef.current = true;
      };
      // timout added to prevent auto scroll to not considered as user scroll
      const timeoutId = setTimeout(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
      }, SCROLL_LISTENER_ATTACH_DELAY_MS);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("scroll", handleScroll);
      };
    } else if (!isBackNavigation) {
      sessionStorage.removeItem(HOME_LAST_CLICKED_CHARACTER_SESSION_STORAGE_KEY);
      refUserHasScrolledRef.current = false;
      window.scrollTo(0, 0);
    }
  }, [charHits?.length, stateRecommendations, shouldInjectRecommendations, elementRefs]);
  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: `repeat(auto-fill, minmax(${colWidth}px, 1fr))`,
      gap: colGap,
    }),
    [colWidth, colGap],
  );
  const renderHitCard = (character: HitConfig) => {
    if (collection === Collection.LOREBOOKS) {
      return (
        <LorebookCard
          lorebook={character as unknown as Lorebook}
          navigate={navigate}
          dropdownMenuActions={character?.dropdownActions}
          characterCardActionsHandler={character?.characterCardActionsHandler}
          characterCardActionsState={character?.characterActionsState}
        />
      );
    }
    if (collection === Collection.RULEBOOKS) {
      return (
        <RulebookCard
          rulebook={character as unknown as Rulebook}
          dropdownMenuActions={character?.dropdownActions}
          characterCardActionsHandler={character?.characterCardActionsHandler}
          characterCardActionsState={character?.characterActionsState}
        />
      );
    }
    return (
      <CharacterCard
        membersCount={character.sub_characters_count}
        // @ts-expect-error if collection is not lorebook then character wil have characterId for sure
        characterInfo={character}
        characterCardActionsHandler={character?.characterCardActionsHandler}
        characterCardActionsState={character?.characterActionsState}
        atCenter={centeredElementIds.includes(character?.characterId as string)}
        variant={character?.characterCardVariant}
        dropdownMenuActions={character?.dropdownActions}
      />
    );
  };

  const ShareCapabilityModal = shareCapabilityModalFor(collection);
  const isNonCharacterCollection = collection === Collection.LOREBOOKS || collection === Collection.RULEBOOKS;

  if (error && !isHitsLoading) {
    return <ErrorSection shouldCenter={true} />;
  }
  const totalNumberOfRows = visibleHits.length ? visibleHits.length / numberOfColumns : 0;
  const shouldShowInlineBanner = (index: number) => {
    const itemRowNumber = index / numberOfColumns;
    return totalNumberOfRows > itemRowNumber && !isTagsSelected;
  };

  const rowMapping = getInlineBannersRowMapping(CmsBannerService.contentfulScreen as ContentFulScreens);
  const shouldShowLoading = isDataLoading || hasNoResults;

  return (
    <>
      {/* Putting loading condition here, as during conditional rendering , ref receieves null, and we can't calculate container width */}
      <div className="flex flex-col grow" ref={refCharacterCardContainerRef}>
        {shouldShowLoading ? (
          <div className="grow flex justify-center">
            <ListEmpty
              isLoading={isDataLoading}
              title={emptyState.title}
              description={emptyState.description}
              buttonText={emptyState?.buttonText}
              onClick={emptyState?.onButtonPress}
            />
          </div>
        ) : (
          <div
            className={cx({
              "w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4": collection === Collection.LOREBOOKS,
              "w-full grid grid-cols-1 md:grid-cols-2 gap-4 pt-4": collection === Collection.RULEBOOKS,
              grid: !isNonCharacterCollection,
            })}
            style={isNonCharacterCollection ? undefined : gridStyle}
          >
            {visibleHits
              ?.filter((character) => !character?.isHidden)
              ?.map((character, index) => {
                return (
                  <React.Fragment key={character.uniqueKey}>
                    {rowMapping[index / numberOfColumns]?.length && shouldShowInlineBanner(index) ? (
                      <div className=" w-full col-span-full">
                        <CmsBanner placement={BannerType.Inline} isVisibleOnPartner={false} availableBanners={rowMapping[index / numberOfColumns]} />
                      </div>
                    ) : null}
                    <div ref={(el) => setElementRef(character?.id, el)}>{renderHitCard(character)}</div>
                  </React.Fragment>
                );
              })}
          </div>
        )}
      </div>
      <ShareCapabilityModal
        visible={stateIsShareModalOpen}
        setShowShare={setStateIsShareModalOpen}
        characterId={characterId}
        characterName={stateCharacterInfo?.name}
        shareUrl={`${window.location.origin}/${shareSegmentFor(collection)}/${characterId}`}
      />
      {!!stateIsBlockModalOpen && (
        <BlockCreatorModal
          creatorId={stateCharacterInfo?.creator_user_id as string}
          creatorUsername={stateCharacterInfo?.creator_username as string}
          isVisible={stateIsBlockModalOpen}
          onClose={handleCloseBlockModal}
        />
      )}

      {stateAttachRulebook ? (
        <RulebookAttachTargetModal
          isOpen
          onClose={handleCloseAttachRulebook}
          rulebookId={stateAttachRulebook.id}
          rulebookName={stateAttachRulebook.name}
        />
      ) : null}
      <AgeVerificationWarningModal version="chat" isOpen={stateIsAgeVerificationVisible} onClose={() => setStateIsAgeVerificationVisible(false)} />
      {isCloning ? <ScreenLoader /> : null}
    </>
  );
});

Hits.displayName = "Hits";

export default Hits;
